import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { HomeSectionHeader } from "@/components/home/HomeSection";
import { useLang } from "@/context/LangContext";

/**
 * Reusable home feed — header + horizontal carousel or responsive grid.
 * Expects to sit inside HomeSection (no outer section chrome).
 */
export function HomeDynamicCarousel({
  kicker,
  title,
  subtitle,
  linkTo,
  linkLabel,
  loading = false,
  items = [],
  renderItem,
  skeletonCount = 4,
  headerAlign = "left",
}) {
  const { t } = useLang();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    containScroll: "trimSnaps",
    dragFree: true,
    duration: 28,
    breakpoints: {
      "(min-width: 640px)": { align: "start" },
    },
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const syncNav = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    syncNav();
    emblaApi.on("select", syncNav);
    emblaApi.on("reInit", syncNav);
    return () => {
      emblaApi.off("select", syncNav);
      emblaApi.off("reInit", syncNav);
    };
  }, [emblaApi, syncNav]);

  if (!loading && items.length === 0) return null;

  const useCarousel = items.length > 3;

  const nav = (
    <div className="flex shrink-0 items-center gap-2">
      {useCarousel && !loading ? (
        <>
          <button
            type="button"
            aria-label={t("a11y.carousel_prev")}
            disabled={!canPrev}
            onClick={() => emblaApi?.scrollPrev()}
            className="matpass-btn inline-flex h-11 w-11 min-h-11 min-w-11 items-center justify-center rounded-full border border-matpro-border bg-white text-matpro-anthracite hover:bg-matpro-section disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label={t("a11y.carousel_next")}
            disabled={!canNext}
            onClick={() => emblaApi?.scrollNext()}
            className="matpass-btn inline-flex h-11 w-11 min-h-11 min-w-11 items-center justify-center rounded-full border border-matpro-border bg-white text-matpro-anthracite hover:bg-matpro-section disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </>
      ) : null}
      {linkTo && linkLabel ? (
        <Link
          to={linkTo}
          className="text-sm font-bold text-matpro-green hover:text-matpro-green-dark"
        >
          {linkLabel}
        </Link>
      ) : null}
    </div>
  );

  return (
    <>
      <HomeSectionHeader
        kicker={kicker}
        title={title}
        subtitle={subtitle}
        align={headerAlign}
        action={nav}
      />

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} className="h-52 w-full rounded-2xl bg-zinc-200/80" />
          ))}
        </div>
      ) : useCarousel ? (
        <div
          ref={emblaRef}
          className={cn("overflow-hidden -mx-1 px-1")}
          role="region"
          aria-roledescription="carousel"
          aria-label={title || t("a11y.carousel_region")}
        >
          <div className="flex gap-4">
            {items.map((item, index) => (
              <div
                key={item.id || index}
                className="min-w-0 shrink-0 grow-0 basis-[min(100%,320px)] sm:basis-[min(85%,300px)] lg:basis-[calc(25%-12px)]"
              >
                {renderItem(item, index)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, index) => renderItem(item, index))}
        </div>
      )}
    </>
  );
}
